import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SunHorizon, ListChecks, X, ArrowRight } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { isOverdue, localToday, fmtDue, ticketHref } from "./InboxThread";

export function pendingSummary(data, uid) {
  const today = localToday();
  const tasks = data.received.filter((m) => m.kind === "task" && m.task_status !== "done");
  return {
    tasks,
    overdue: tasks.filter(isOverdue),
    dueToday: tasks.filter((m) => m.due_at === today),
    unread: [...data.received, ...data.sent].filter((m) => (m.unread_for || []).includes(uid) && (m.kind === "message" || m.from_id === uid)),
    tickets: data.my_tickets,
  };
}

const greeting = () => { const h = new Date().getHours(); return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening"; };

export function MorningSummary() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [sum, setSum] = useState(null);
  const key = `morning-summary-${user?.id}`;

  useEffect(() => {
    if (!user?.id || localStorage.getItem(key) === localToday()) return;
    api.get("/inbox").then((r) => {
      const s = pendingSummary(r.data, user.id);
      if (s.tasks.length) setSum(s);
      else localStorage.setItem(key, localToday());
    }).catch(() => {});
  }, [user?.id, key]);

  if (!sum) return null;
  const close = () => { localStorage.setItem(key, localToday()); setSum(null); };
  const go = (path) => { close(); nav(path); };
  const tasks = [...sum.overdue, ...sum.tasks.filter((m) => !isOverdue(m))].slice(0, 5);

  return (
    <div className="fixed inset-0 z-[9500] bg-slate-950/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="morning-summary">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden rh-fade-in">
        <div className="px-6 pt-6 pb-4 bg-gradient-to-br from-[#EFF6FF] to-white relative">
          <button onClick={close} className="absolute top-4 right-4 w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:bg-white" data-testid="morning-summary-close"><X size={16} weight="bold" /></button>
          <SunHorizon size={30} weight="duotone" className="text-amber-500" />
          <div className="mt-2 text-[20px] font-bold text-slate-900">{greeting()}, {user?.name?.split(" ")[0]}</div>
          <div className="text-[13px] text-slate-600 mt-0.5" data-testid="ms-tasks">
            You have <b className="text-slate-900">{sum.tasks.length} task{sum.tasks.length === 1 ? "" : "s"}</b> waiting
            {sum.overdue.length ? <> · <b className="text-rose-600">{sum.overdue.length} overdue</b></> : sum.dueToday.length ? <> · {sum.dueToday.length} due today</> : null}
          </div>
        </div>
        <div className="px-6 py-4 space-y-2 max-h-[280px] overflow-y-auto">
          {tasks.map((m) => (
            <button key={m.id} onClick={() => go(m.ticket_id ? ticketHref(m.ticket_id, { task: m.id }) : "/dashboard")} data-testid={`ms-task-${m.id}`}
              className="w-full text-left flex items-start gap-2.5 rounded-xl border border-[#F1F5F9] px-3 py-2.5 hover:border-[#BFDBFE] transition-colors">
              <ListChecks size={16} weight="bold" className="text-[#1E5FEB] mt-0.5 shrink-0" />
              <span className="flex-1 min-w-0">
                <span className="block text-[13px] text-slate-900 line-clamp-1">{m.text}</span>
                <span className="block text-[11px] text-slate-500">From {m.from_name}{m.ticket_number ? ` · ${m.ticket_number}` : ""}
                  {m.due_at && <span className={isOverdue(m) ? "text-rose-600 font-semibold" : ""}> · {isOverdue(m) ? "Overdue" : "Due"} {fmtDue(m.due_at)}</span>}</span>
              </span>
            </button>
          ))}
        </div>
        <div className="flex gap-2 p-4 border-t border-[#E5E7EB] bg-[#FAFBFC]">
          <button onClick={close} className="flex-1 h-11 rounded-xl border border-[#E5E7EB] bg-white hover:bg-slate-50 text-[13px] font-semibold text-slate-700 transition-colors" data-testid="morning-summary-got-it">Got it</button>
          <button onClick={() => go("/dashboard")} className="flex-1 h-11 rounded-xl bg-[#1E5FEB] hover:bg-[#1849C8] text-white text-[13px] font-bold inline-flex items-center justify-center gap-1.5 transition-colors" data-testid="morning-summary-open-inbox">Open my inbox <ArrowRight size={14} weight="bold" /></button>
        </div>
      </div>
    </div>
  );
}

export function InboxBanner({ data, uid, onShowTasks }) {
  if (!data) return null;
  const s = pendingSummary(data, uid);
  if (!s.tasks.length && !s.unread.length) return null;
  const parts = [
    s.tasks.length && `${s.tasks.length} open task${s.tasks.length === 1 ? "" : "s"}`,
    s.dueToday.length && `${s.dueToday.length} due today`,
    s.overdue.length && `${s.overdue.length} overdue`,
    s.unread.length && `${s.unread.length} unread message${s.unread.length === 1 ? "" : "s"}`,
  ].filter(Boolean);
  const urgent = s.overdue.length > 0;
  return (
    <div className={`rounded-2xl border px-5 py-3.5 flex flex-wrap items-center justify-between gap-3 ${urgent ? "border-rose-200 bg-rose-50" : "border-[#BFDBFE] bg-[#EFF6FF]"}`} data-testid="inbox-banner">
      <div className="flex items-center gap-3 min-w-0">
        <span className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${urgent ? "bg-rose-100" : "bg-white"}`}><ListChecks size={18} weight="bold" className={urgent ? "text-rose-600" : "text-[#1E5FEB]"} /></span>
        <div className={`text-[13.5px] font-semibold ${urgent ? "text-rose-800" : "text-slate-800"}`} data-testid="inbox-banner-text">You have {parts.join(" · ")}</div>
      </div>
      <button onClick={onShowTasks} data-testid="inbox-banner-view" className={`h-9 px-4 rounded-xl text-[12.5px] font-bold transition-colors ${urgent ? "bg-rose-600 hover:bg-rose-700 text-white" : "bg-[#1E5FEB] hover:bg-[#1849C8] text-white"}`}>View to do</button>
    </div>
  );
}
